import React, { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { API, useAuth, useTheme } from "@/App";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Zap, CheckCircle2, ShieldCheck, Sparkles, MessageCircle,
  Clock, ArrowRight, RefreshCw, Send, Check, AlertCircle,
  Lock, Calendar, CreditCard, ChevronRight, HelpCircle,
  Activity, TrendingUp, Cpu, Award, Users, ChevronDown,
  PhoneCall, BarChart3, LineChart, Layers, Flame, CheckCircle
} from "lucide-react";
import { toast } from "sonner";
import { loadRazorpaySDK } from "@/lib/payment";

export default function DrishtiPaywallGate({ featureName = "Drishti AI Quantitative Terminal", onAccessGranted }) {
  const { user, checkAuth } = useAuth();
  const { theme } = useTheme();
  const isLight = theme === "light";

  const [requestStatus, setRequestStatus] = useState("checking");
  const [requestDetails, setRequestDetails] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [checking, setChecking] = useState(false);
  const [manualDrawerOpen, setManualDrawerOpen] = useState(false);

  const ADMIN_PHONES = ["9257059361", "09257059361", "919257059361"];
  const isDeepak = (user?.email || "").toLowerCase() === "deepaksethia@jamadhan.in";

  const getSanitizedPhone = (rawPhone) => {
    const digits = (rawPhone || "").replace(/\D/g, "").slice(-10);
    if (!digits || digits.length < 10) return "";
    if (ADMIN_PHONES.includes(digits) && !isDeepak) return "";
    return digits;
  };

  const [phone, setPhone] = useState(() => getSanitizedPhone(user?.phone));
  const [editingPhone, setEditingPhone] = useState(() => !getSanitizedPhone(user?.phone));

  useEffect(() => {
    const sanitized = getSanitizedPhone(user?.phone);
    if (sanitized) {
      setPhone(sanitized);
      setEditingPhone(false);
    } else {
      setPhone("");
      setEditingPhone(true);
    }
  }, [user?.phone, user?.email]);

  // Calculate dynamic 90 days date
  const billingDates = useMemo(() => {
    const now = new Date();
    const nextDate = new Date();
    nextDate.setDate(now.getDate() + 90);
    return {
      todayFormatted: now.toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }),
      firstDebitFormatted: nextDate.toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })
    };
  }, []);

  const checkRequestStatus = useCallback(async (quiet = false) => {
    if (!quiet) setChecking(true);
    try {
      const res = await axios.get(`${API}/drishti/my-access-request`, { withCredentials: true });
      if (res.data?.has_access === true || res.data?.status === "approved") {
        setRequestStatus("approved");
        if (checkAuth) await checkAuth();
        if (onAccessGranted) onAccessGranted();
      } else if (res.data?.status === "pending") {
        setRequestStatus("pending");
        setRequestDetails(res.data);
      } else if (res.data?.status === "rejected") {
        setRequestStatus("rejected");
        setRequestDetails(res.data);
      } else if (res.data?.status === "expired") {
        setRequestStatus("expired");
        setRequestDetails(res.data);
      } else {
        setRequestStatus("not_requested");
      }
    } catch (err) {
      setRequestStatus("not_requested");
    } finally {
      if (!quiet) setChecking(false);
    }
  }, [checkAuth, onAccessGranted]);

  useEffect(() => {
    checkRequestStatus(true);
    const pollTimer = setInterval(() => {
      if (!document.hidden) {
        checkRequestStatus(true);
      }
    }, 3000);
    return () => clearInterval(pollTimer);
  }, [checkRequestStatus]);

  // Request Access from Admin (Option 2)
  const handleRequestAccess = async () => {
    setSubmitting(true);
    try {
      const res = await axios.post(
        `${API}/drishti/request-access`,
        { plan_id: "drishti_trial_autopay" },
        { withCredentials: true }
      );
      toast.success(res.data?.message || "Access request submitted to admin Deepak!");
      setRequestStatus("pending");
      setRequestDetails({
        plan_id: "drishti_trial_autopay",
        plan_name: "3 Months Free Access Pass",
        created_at: new Date().toISOString()
      });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to submit access request. Try WhatsApp.");
    } finally {
      setSubmitting(false);
    }
  };

  // Online Checkout via Razorpay Subscriptions (₹1 Setup Fee + 90 Days Free Access + ₹1,000/mo Auto-Debit from Day 90)
  const handleOnlinePay = async () => {
    const cleanPhone = phone.replace(/\D/g, "").slice(-10);
    if (!cleanPhone || cleanPhone.length < 10) {
      toast.error("Please enter your 10-digit mobile number for Razorpay authorization.");
      setEditingPhone(true);
      return;
    }
    if (ADMIN_PHONES.includes(cleanPhone) && !isDeepak) {
      toast.error("The helpline number (+91 9257059361) cannot be used. Please enter your personal mobile number.");
      setEditingPhone(true);
      return;
    }

    setSubmitting(true);
    const dismissLoading = toast.loading("Connecting to secure Razorpay checkout...");

    try {
      const res = await axios.post(
        `${API}/payments/razorpay/create-subscription`,
        { customer_phone: cleanPhone },
        { withCredentials: true }
      );

      const data = res.data;
      if (data.already_active) {
        toast.dismiss(dismissLoading);
        toast.success(data.message || "Your Drishti AI access is active!");
        setRequestStatus("approved");
        if (checkAuth) await checkAuth();
        if (onAccessGranted) onAccessGranted();
        setSubmitting(false);
        return;
      }

      const { subscription_id, key_id } = data;
      if (!subscription_id || !key_id) {
        throw new Error("Razorpay subscription mandate configuration missing.");
      }

      const RazorpayClass = await loadRazorpaySDK();
      if (!RazorpayClass) {
        toast.dismiss(dismissLoading);
        toast.error("Razorpay checkout failed to load. Please check your network connection.");
        setSubmitting(false);
        return;
      }

      toast.dismiss(dismissLoading);

      const options = {
        key: key_id,
        subscription_id: subscription_id,
        name: "JamaDhan Institute LLP",
        description: "Drishti AI · 3 Months Free Access (₹1 Setup Fee)",
        image: "/jamadhan-logo.png",
        prefill: {
          name: user?.name || "JamaDhan Trader",
          email: user?.email || "",
          contact: cleanPhone
        },
        theme: {
          color: "#00FF88",
          backdrop_color: "rgba(0, 0, 0, 0.95)"
        },
        notes: {
          plan: "Drishti AI Pro 3 Months Free Access Pass",
          app: "jamadhan_drishti"
        },
        handler: async function (response) {
          const verifyToast = toast.loading("Verifying mandate and unlocking terminal...");
          try {
            const verifyRes = await axios.post(
              `${API}/payments/razorpay/verify-subscription`,
              {
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_subscription_id: response.razorpay_subscription_id || subscription_id,
                razorpay_signature: response.razorpay_signature
              },
              { withCredentials: true }
            );

            toast.dismiss(verifyToast);

            if (verifyRes.data?.success || verifyRes.data?.status === "active" || verifyRes.data?.status === "authenticated") {
              try {
                sessionStorage.removeItem("jamadhan_referral_code");
                localStorage.removeItem("jamadhan_referral_code");
              } catch (_) {}
              toast.success("Mandate Verified! 90 Days Drishti AI Pro Unlocked.");
              setRequestStatus("approved");
              if (checkAuth) await checkAuth();
              if (onAccessGranted) onAccessGranted();
            } else {
              toast.info(verifyRes.data?.message || "Payment verified! Unlocking terminal...");
              if (checkAuth) await checkAuth();
              if (onAccessGranted) onAccessGranted();
            }
          } catch (verErr) {
            toast.dismiss(verifyToast);
            try { if (checkAuth) await checkAuth(); } catch (_) {}
            toast.error(verErr.response?.data?.detail || "Mandate verified! Please refresh if terminal does not reload immediately.");
          } finally {
            setSubmitting(false);
          }
        },
        modal: {
          ondismiss: function () {
            toast.info("Payment window dismissed.");
            setSubmitting(false);
          }
        }
      };

      const rzpInstance = new RazorpayClass(options);
      rzpInstance.open();

    } catch (err) {
      toast.dismiss(dismissLoading);
      toast.error(err.response?.data?.detail || err.message || "Failed to initialize Razorpay checkout.");
      setSubmitting(false);
    }
  };

  const handleWhatsAppContact = () => {
    const text = encodeURIComponent(
      `Hello Deepak Sir, I would like to activate my Drishti AI Pro terminal (3 Months Free Access - ₹1 Setup Fee). Please review and approve my account: ${user?.email || user?.name || "Trader"}.`
    );
    window.open(`https://wa.me/919257059361?text=${text}`, "_blank");
  };

  return (
    <div className="w-full max-w-4xl mx-auto py-2 sm:py-6 px-3 sm:px-6 space-y-6 sm:space-y-8 select-none text-slate-100 font-sans" data-testid="drishti-paywall-gate">
      
      {/* ═══ APPLE MUSIC / SPOTIFY STYLE HERO BANNER ═══ */}
      <div className="relative overflow-hidden rounded-[2rem] sm:rounded-[2.5rem] p-6 sm:p-10 border border-white/[0.08] bg-gradient-to-b from-zinc-950/90 via-zinc-950 to-black shadow-[0_30px_90px_rgba(0,0,0,0.95)]">
        {/* Soft Ambient Aurora Gradient Backgrounds */}
        <div className="absolute top-0 right-0 w-[350px] sm:w-[500px] h-[350px] sm:h-[500px] bg-gradient-to-br from-emerald-500/20 via-teal-500/10 to-transparent rounded-full blur-[100px] pointer-events-none -mr-20 -mt-20" />
        <div className="absolute bottom-0 left-0 w-[250px] sm:w-[350px] h-[250px] sm:h-[350px] bg-emerald-600/10 rounded-full blur-[90px] pointer-events-none -ml-16 -mb-16" />
        <div className="absolute inset-0 bg-[radial-gradient(#ffffff_1px,transparent_1px)] [background-size:24px_24px] opacity-[0.03] pointer-events-none" />

        <div className="relative z-10 max-w-2xl mx-auto text-center space-y-3 sm:space-y-4">
          
          {/* Subtle Live Status Pill */}
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/[0.04] border border-white/[0.08] backdrop-blur-xl">
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#00FF88] opacity-75" />
              <span className="relative inline-flex rounded-full h-2 w-2 bg-[#00FF88]" />
            </span>
            <span className="text-[10px] sm:text-xs font-semibold tracking-wider text-emerald-400 uppercase font-mono">
              Quantitative Algorithmic Engine · Active
            </span>
          </div>

          {/* Apple-grade Headline */}
          <h1 className="text-2xl sm:text-4xl lg:text-5xl font-black tracking-tight text-white leading-tight">
            Institutional Signals.{" "}
            <span className="bg-gradient-to-r from-[#00FF88] via-emerald-400 to-cyan-400 bg-clip-text text-transparent">
              90 Days Free.
            </span>
          </h1>

          <p className="text-xs sm:text-sm text-zinc-400 max-w-lg mx-auto leading-relaxed">
            Real-time algorithmic 15m & 1h Demand & Supply zones, exact entries, stop-loss and dual profit targets. Set up your access today for just <span className="text-white font-bold">₹1 once</span>.
          </p>
        </div>
      </div>

      {/* Access Failure Notice if any */}
      {user?.drishti_subscription?.failure_reason && (
        <div className="rounded-2xl p-4 border border-rose-500/40 bg-rose-500/10 text-rose-200 flex items-start gap-3 shadow-lg">
          <AlertCircle className="w-5 h-5 text-rose-400 shrink-0 mt-0.5" />
          <div className="text-xs sm:text-sm">
            <p className="font-bold text-white">Monthly Renewal Mandate Paused</p>
            <p className="text-rose-300/90 mt-0.5">{user.drishti_subscription.failure_reason}. Re-authorize your card or UPI AutoPay below to resume signals.</p>
          </div>
        </div>
      )}

      {/* Pending Admin Request Banner */}
      {requestStatus === "pending" && (
        <div className="rounded-2xl p-4 sm:p-5 border border-amber-500/30 bg-amber-500/10 backdrop-blur-md">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-xl bg-amber-500/20 border border-amber-500/30 flex items-center justify-center text-amber-400 shrink-0">
                <Clock className="w-5 h-5 animate-spin" />
              </div>
              <div>
                <h3 className="text-xs sm:text-sm font-bold text-amber-200">
                  Verification Pending Admin Review
                </h3>
                <p className="text-[11px] text-amber-200/80 mt-0.5">
                  Your request is queued for Founder Deepak Sethia. Once approved, this screen unlocks automatically.
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2 w-full sm:w-auto shrink-0">
              <Button
                variant="outline"
                size="sm"
                onClick={() => checkRequestStatus()}
                disabled={checking}
                className="flex-1 sm:flex-none h-9 px-3 text-xs font-bold rounded-xl border-amber-500/30 text-amber-200 hover:bg-amber-500/20"
              >
                <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${checking ? "animate-spin" : ""}`} />
                Check Status
              </Button>
              <Button
                size="sm"
                onClick={handleWhatsAppContact}
                className="flex-1 sm:flex-none h-9 px-3 text-xs font-bold rounded-xl bg-[#25D366] hover:bg-[#25D366]/90 text-white shadow-md"
              >
                <MessageCircle className="w-4 h-4 mr-1.5" />
                WhatsApp
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* ═══ SPOTIFY / APPLE MUSIC HERO CHECKOUT CARD ═══ */}
      <div className="relative rounded-[2rem] sm:rounded-[2.5rem] p-6 sm:p-10 border border-emerald-500/40 bg-gradient-to-b from-[#0D131F] via-[#090D15] to-[#05070B] shadow-[0_20px_70px_rgba(0,255,136,0.1)] overflow-hidden space-y-6">
        
        {/* Subtle Top Emerald Glow Line */}
        <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-[#00FF88] to-transparent" />

        {/* Card Header & Badge */}
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <div className="flex items-center gap-2">
            <span className="text-[10px] sm:text-xs font-extrabold font-mono tracking-wider px-3 py-1 rounded-full uppercase bg-[#00FF88] text-black shadow-[0_0_20px_rgba(0,255,136,0.4)]">
              ★ 3 MONTHS FREE PASS
            </span>
            <span className="text-[10px] sm:text-xs font-mono font-bold text-emerald-400 px-2.5 py-0.5 rounded-full bg-emerald-500/10 border border-emerald-500/20">
              90 DAYS INCLUDED
            </span>
          </div>
          <span className="text-xs font-mono text-zinc-400 flex items-center gap-1.5">
            <ShieldCheck className="w-4 h-4 text-[#00FF88]" />
            Razorpay Secured
          </span>
        </div>

        {/* Pricing Headline */}
        <div>
          <div className="flex items-baseline gap-3 flex-wrap">
            <span className="text-4xl sm:text-6xl font-black font-mono text-[#00FF88] tracking-tight">
              ₹1
            </span>
            <span className="text-sm sm:text-base font-mono text-zinc-500 line-through">
              ₹3,000 standard
            </span>
            <span className="text-xs font-mono font-bold text-zinc-400 uppercase tracking-wider">
              One-Time Setup Fee (Non-Refundable)
            </span>
          </div>
          <p className="text-xs sm:text-sm text-zinc-300 mt-2 leading-relaxed">
            Pay ₹1 today to verify your payment mandate. Enjoy full 90-day institutional access, followed by ₹1,000/month recurring debit starting from <span className="text-emerald-400 font-semibold">{billingDates.firstDebitFormatted}</span> (Day 90).
          </p>
        </div>

        {/* ─── 3-STAGE VISUAL TIMELINE ROADMAP (APPLE STYLE) ─── */}
        <div className="p-4 sm:p-5 rounded-2xl bg-black/50 border border-white/[0.08] backdrop-blur-md">
          <p className="text-[10px] uppercase font-mono tracking-wider text-zinc-400 font-bold mb-3">
            Clear Payment Roadmap · Zero Hidden Terms
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            
            {/* Step 1 */}
            <div className="p-3 rounded-xl bg-white/[0.03] border border-[#00FF88]/30 space-y-1">
              <div className="flex items-center justify-between">
                <span className="text-[10px] font-mono text-zinc-400 uppercase font-bold">1. Today</span>
                <span className="text-xs font-mono font-bold text-[#00FF88]">₹1 Only</span>
              </div>
              <p className="text-xs font-bold text-white">Setup & Verification</p>
              <p className="text-[11px] text-zinc-400">Mandate authorization & instant terminal unlock.</p>
            </div>

            {/* Step 2 */}
            <div className="p-3 rounded-xl bg-white/[0.03] border border-cyan-500/30 space-y-1">
              <div className="flex items-center justify-between">
                <span className="text-[10px] font-mono text-zinc-400 uppercase font-bold">2. Next 90 Days</span>
                <span className="text-xs font-mono font-bold text-cyan-400">₹0 Free</span>
              </div>
              <p className="text-xs font-bold text-white">Full AI Signals</p>
              <p className="text-[11px] text-zinc-400">100% free institutional signals & trading tools.</p>
            </div>

            {/* Step 3 */}
            <div className="p-3 rounded-xl bg-white/[0.03] border border-amber-500/30 space-y-1">
              <div className="flex items-center justify-between">
                <span className="text-[10px] font-mono text-zinc-400 uppercase font-bold">3. From Day 90</span>
                <span className="text-xs font-mono font-bold text-amber-400">₹1,000/mo</span>
              </div>
              <p className="text-xs font-bold text-white">Monthly AutoPay</p>
              <p className="text-[11px] text-zinc-400">First debit on {billingDates.firstDebitFormatted}. Cancel anytime.</p>
            </div>

          </div>
        </div>

        {/* ─── CANCELLATION TRANSPARENCY NOTICE (PROMINENT) ─── */}
        <div className="p-4 rounded-2xl bg-emerald-500/[0.07] border border-emerald-500/20 text-xs space-y-1.5 font-plex">
          <div className="flex items-center gap-2 text-emerald-400 font-bold">
            <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0" />
            <span>Where & How You Can Cancel Anytime:</span>
          </div>
          <p className="text-zinc-300 text-[11px] sm:text-xs leading-relaxed pl-6">
            You can cancel the AutoPay mandate with <span className="font-bold text-white">1 click in your Profile or Payments page</span> anytime before Day 90. If you cancel, <span className="font-bold text-emerald-300">you will never be debited ₹1,000</span>, and your full 90-day free access will remain active until the 90th day.
          </p>
        </div>

        {/* ─── FEATURES CHECKLIST ─── */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 pt-1">
          {[
            "15m & 1h Algorithmic Demand & Supply Zones live on chart",
            "Exact Entry, Stop Loss & dual Target levels for 40+ liquid scrips",
            "Works with UPI AutoPay (GPay, PhonePe, Paytm) & Credit/Debit Cards",
            "1-Click Trade Bridge with Dhan Terminal & Capital Risk Calculator"
          ].map((item, idx) => (
            <div key={idx} className="flex items-start gap-2.5 text-xs text-zinc-300">
              <div className="w-4 h-4 rounded-full bg-[#00FF88]/15 text-[#00FF88] flex items-center justify-center shrink-0 mt-0.5">
                <Check className="w-3 h-3 stroke-[3]" />
              </div>
              <span>{item}</span>
            </div>
          ))}
        </div>

        {/* ─── MOBILE NUMBER MANDATE SECTION ─── */}
        <div className="p-3.5 sm:p-4 rounded-2xl bg-white/[0.03] border border-white/[0.08]">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
            <div className="space-y-0.5">
              <div className="flex items-center gap-2">
                <span className="text-xs font-bold text-white">Mobile for UPI AutoPay</span>
                <span className="text-[9px] font-mono px-1.5 py-0.2 rounded bg-[#00FF88]/10 text-[#00FF88] border border-[#00FF88]/20 font-bold uppercase">
                  Required
                </span>
              </div>
              <p className="text-[11px] text-zinc-400">
                Your bank mandate will be issued to this mobile number.
              </p>
            </div>

            {editingPhone || !phone || phone.length < 10 ? (
              <div className="flex items-center gap-2 w-full sm:w-auto">
                <div className="relative flex-1 sm:w-44">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-xs font-mono font-bold text-zinc-500">
                    +91
                  </span>
                  <input
                    type="tel"
                    maxLength={10}
                    value={phone}
                    onChange={(e) => setPhone(e.target.value.replace(/\D/g, "").slice(0, 10))}
                    placeholder="10-digit mobile"
                    className="w-full pl-11 pr-3 py-1.5 text-xs font-mono font-bold rounded-xl border bg-black/80 border-white/20 text-[#00FF88] focus:border-[#00FF88] outline-none"
                  />
                </div>
                <button
                  type="button"
                  onClick={() => {
                    if (phone.length === 10) setEditingPhone(false);
                    else toast.error("Please enter a valid 10-digit mobile number.");
                  }}
                  disabled={phone.length < 10}
                  className="text-xs px-3.5 py-1.5 bg-[#00FF88] hover:bg-[#00FF88]/90 text-black font-bold rounded-xl disabled:opacity-40 transition-all shadow-md shrink-0 cursor-pointer"
                >
                  Save
                </button>
              </div>
            ) : (
              <div className="flex items-center gap-2.5 bg-white/[0.05] px-3.5 py-1.5 rounded-xl border border-white/[0.08] w-full sm:w-auto justify-between sm:justify-start">
                <span className="text-xs font-mono font-bold text-[#00FF88]">
                  +91 {phone}
                </span>
                <button
                  type="button"
                  onClick={() => setEditingPhone(true)}
                  className="text-[11px] text-zinc-400 hover:text-white underline cursor-pointer"
                >
                  Change
                </button>
              </div>
            )}
          </div>
        </div>

        {/* ─── HIGH CONVERTING PRIMARY CTA ─── */}
        <div className="pt-2 space-y-3">
          <Button
            size="lg"
            onClick={handleOnlinePay}
            disabled={submitting}
            className="w-full h-13 sm:h-14 font-extrabold text-sm sm:text-base bg-[#00FF88] hover:bg-[#00FF88]/90 !text-black shadow-[0_0_50px_rgba(0,255,136,0.4)] rounded-2xl transition-all duration-300 hover:scale-[1.01] active:scale-[0.99] cursor-pointer"
            data-testid="pay-online-razorpay-btn"
          >
            {submitting ? (
              <span className="flex items-center gap-2">
                <Clock className="w-5 h-5 animate-spin" /> Authorizing with Razorpay...
              </span>
            ) : (
              <span className="flex items-center justify-center gap-2">
                <Zap className="w-5 h-5 fill-current" />
                <span>Pay ₹1 & Get 90-Day Free Access</span>
                <ArrowRight className="w-5 h-5 ml-1" />
              </span>
            )}
          </Button>

          <div className="flex flex-wrap items-center justify-center gap-3 text-[11px] text-zinc-400">
            <span className="flex items-center gap-1">
              <ShieldCheck className="w-3.5 h-3.5 text-[#00FF88]" /> Razorpay 256-Bit SSL
            </span>
            <span>•</span>
            <span>₹1 Setup Fee Today</span>
            <span>•</span>
            <span>Cancel 1-Click Anytime in Profile</span>
          </div>
        </div>

      </div>

      {/* ─── OPTION 2: JAIPUR BATCH & DIRECT NEFT (CLEAN DRAWER) ─── */}
      <div className="rounded-2xl border border-white/[0.06] bg-black/40 p-4 sm:p-5">
        <button
          type="button"
          onClick={() => setManualDrawerOpen(!manualDrawerOpen)}
          className="w-full flex items-center justify-between text-left cursor-pointer"
        >
          <div className="flex items-center gap-2.5">
            <div className="w-7 h-7 rounded-lg bg-amber-500/10 text-amber-400 flex items-center justify-center">
              <Lock className="w-3.5 h-3.5" />
            </div>
            <div>
              <p className="text-xs sm:text-sm font-bold text-white">
                Offline Jaipur Batch or Direct Bank Transfer?
              </p>
              <p className="text-[11px] text-zinc-400">
                Click here if you are paying via NEFT/bank transfer or attending in-person.
              </p>
            </div>
          </div>
          <ChevronDown className={`w-4 h-4 text-zinc-400 transition-transform ${manualDrawerOpen ? "rotate-180" : ""}`} />
        </button>

        {manualDrawerOpen && (
          <div className="pt-4 mt-3 border-t border-white/[0.06] space-y-3 text-xs animate-in fade-in">
            <p className="text-zinc-300">
              Attending the offline batch in Jaipur or paying directly? Submit your account details and Founder Deepak Sethia will approve your access:
            </p>
            <div className="flex flex-col sm:flex-row gap-2">
              <Button
                size="sm"
                onClick={handleRequestAccess}
                disabled={submitting || requestStatus === "pending"}
                className="flex-1 h-10 font-bold text-xs rounded-xl bg-white/10 hover:bg-white/15 text-white border border-white/10"
              >
                {requestStatus === "pending" ? "Pending Admin Approval..." : "Request Manual Access"}
              </Button>
              <Button
                size="sm"
                onClick={handleWhatsAppContact}
                className="flex-1 h-10 font-bold text-xs rounded-xl bg-[#25D366] hover:bg-[#25D366]/90 !text-white"
              >
                <MessageCircle className="w-4 h-4 mr-1.5" />
                WhatsApp Deepak Sir
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* ═══ FAQ SECTION (CONVERTER FOCUSED) ═══ */}
      <div className="rounded-3xl border border-white/[0.06] bg-[#070A11] p-5 sm:p-7 space-y-4">
        <h3 className="text-base sm:text-lg font-bold text-white text-center">
          Frequently Asked Questions
        </h3>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 pt-1">
          {[
            {
              q: "Why am I being charged ₹1 today?",
              a: "The ₹1 is a one-time non-refundable setup fee to authorize and verify your UPI AutoPay mandate with Razorpay and the banking network. It confirms your account and unlocks your full 90-day access instantly."
            },
            {
              q: "When will the ₹1,000 monthly debit begin?",
              a: `Your first monthly debit of ₹1,000 will occur on ${billingDates.firstDebitFormatted} (Day 90). You will not be charged anything between now and Day 90.`
            },
            {
              q: "Where and how can I cancel the AutoPay?",
              a: "You can cancel your AutoPay mandate with a single click directly from your Profile or Payments page at any time before Day 90. If cancelled, no ₹1,000 debit will ever take place, and your 90 days access stays valid."
            },
            {
              q: "What payment methods are supported for AutoPay?",
              a: "All major UPI apps (Google Pay, PhonePe, Paytm, BHIM), Indian Debit Cards (with eMandate), and Credit Cards via Razorpay."
            }
          ].map((faq, i) => (
            <div key={i} className="p-3.5 rounded-2xl bg-white/[0.02] border border-white/[0.04] space-y-1">
              <p className="text-xs font-bold text-white flex items-center gap-2">
                <CheckCircle className="w-3.5 h-3.5 text-[#00FF88] shrink-0" />
                {faq.q}
              </p>
              <p className="text-[11px] text-zinc-400 leading-relaxed pl-5.5">
                {faq.a}
              </p>
            </div>
          ))}
        </div>
      </div>

    </div>
  );
}
